import {useTranslation} from 'react-i18next';

import {Box, Typography} from '@mui/material';

import {WORKFORCE_DASHBOARD_SCROLLABLE_SECTION_MAX_HEIGHT} from './workforceDashboardLayoutConstants';
import {WorkforceDashboardSectionCard} from './WorkforceDashboardSectionCard';
import {EmptyStateCard} from '../../../../../components/EmptyStateCard/EmptyStateCard';
import {TableRowScorePill} from '../../../../../components/TableRowScorePill/TableRowScorePill';
import {WorkforceDashboardActionItem} from '../../types/workforceDashboardTypes';

export interface Props {
  data: WorkforceDashboardActionItem[] | null;
  onProfileClick: (resourceId: string) => void;
}

export const WorkforceDashboardActionList = ({data, onProfileClick}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'workforceDashboardPage.sections.profilesRequiringAction'});

  if (!data || data.length === 0) {
    return (
      <WorkforceDashboardSectionCard title={t('title')}>
        <EmptyStateCard message={t('emptyMessage') as string} />
      </WorkforceDashboardSectionCard>
    );
  }

  return (
    <WorkforceDashboardSectionCard
      title={t('title')}
      description={t('description') as string}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          minWidth: 0,
          maxHeight: WORKFORCE_DASHBOARD_SCROLLABLE_SECTION_MAX_HEIGHT,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {data.map((item) => (
          <Box
            key={item.resourceId}
            onClick={() => onProfileClick(item.resourceId)}
            sx={{
              cursor: 'pointer',
              p: 1.25,
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {bgcolor: 'action.hover'},
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box sx={{minWidth: 0, flex: 1}}>
              <Typography variant='h6' sx={{overflowWrap: 'anywhere'}}>
                {item.publicId} → {item.targetProfileTitle}
              </Typography>
              <Typography variant='caption' sx={{color: 'text.secondary', overflowWrap: 'anywhere'}}>
                {t('itemSummary', {department: item.companyDepartmentName, gaps: item.gapCount})}
              </Typography>
            </Box>
            <Box sx={{flexShrink: 0}}>
              <TableRowScorePill score={item.overallScore} />
            </Box>
          </Box>
        ))}
      </Box>
    </WorkforceDashboardSectionCard>
  );
};
